import React, { createContext, useCallback, useContext, useEffect, useMemo, useState, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { logoutSession, validateSession } from '../api/auth';

interface AuthContextType {
    isAuthenticated: boolean | null;
    setAuthenticated: (value: boolean) => void;
    logout: () => Promise<void>;
    checkAuth: () => Promise<boolean>;
}

const AuthContext = createContext<AuthContextType>({
    isAuthenticated: null,
    setAuthenticated: () => {},
    logout: async () => {},
    checkAuth: async () => false,
});

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const navigate = useNavigate();
    const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);

    const checkAuth = useCallback(async () => {
        const valid = await validateSession();
        setIsAuthenticated(valid);
        return valid;
    }, []);

    const logout = useCallback(async () => {
        try {
            await logoutSession();
        } catch {
            // Still clear local session state if the request fails.
        }
        setIsAuthenticated(false);
        navigate('/login');
    }, [navigate]);

    useEffect(() => {
        checkAuth();
    }, [checkAuth]);

    const value = useMemo(
        () => ({
            isAuthenticated,
            setAuthenticated: setIsAuthenticated,
            logout,
            checkAuth,
        }),
        [isAuthenticated, logout, checkAuth]
    );

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);
